/**
 * Runs the site's own single-item customization engine (the one behind the
 * item page's "Customize" section — src/containers/OtherManuItems +
 * src/containers/ItemDetail) for a Berry card, so a salad tray customized in
 * Berry follows exactly the production rules: included ingredients that can
 * be removed or doubled, substitution groups (Greens), per-modifier and
 * per-group limits, "No dressing", priced extras, and the cart payload codes.
 *
 * Nothing here re-implements a pricing or limit rule. The engine
 * (src/priceCalculation, src/helper/customizedItem) and the
 * itemCustomization reducer are imported and called the same way the item
 * page calls them; the few functions below that are copies are the page's
 * private glue (not exported from those files), copied for the non-edit,
 * non-package path only and marked with their source.
 *
 * Isolation — the site keeps this state in two global places:
 *  - the `itemCustomization` Redux slice → Berry uses a private store
 *    (`createSiteCustomizationStore`), so a customer on an item page keeps
 *    their own in-progress customization;
 *  - the `itemBuckets` singleton → snapshotted before Berry starts and put
 *    back when the picker closes (`snapshotItemBuckets`/`restoreItemBuckets`).
 */
import _clonedeep from 'lodash.clonedeep';
import { combineReducers, configureStore } from '@reduxjs/toolkit';

import { userSliceReducer } from 'src/Features/AccountSetup/redux/slices';
import { locationSliceReducer } from 'src/Features/Location/redux/slice';
import * as CONSTANT from 'src/constants';
import {
  availableModifiers,
  createOrderPayloadModifiers,
  createPayloadToSync,
  sortBasedOnPriceByModifierGroups,
  unavailableModifiers,
} from 'src/helper/customizedItem/customizedItem.helper';
import { getCustomizedItemPrice } from 'src/helper/getCombinePrice/getCombinePrice.helper';
import { creatingModifiersForItems, itemSize } from 'src/helper/helperMethods';
import { Codes } from 'src/models/item.model';
import { itemBuckets } from 'src/priceCalculation/buckets';
import { calculatePriceForItem } from 'src/priceCalculation/calculatePrice';
import { modifiersAgainstAnItem, toFixedNumber } from 'src/priceCalculation/helper';
import { addPaymentSliceReducer } from 'src/redux/slices/addPayment/addPaymentSlice';
import { reducer as cartReducer } from 'src/redux/slices/cartSlice';
import { checkoutReducer } from 'src/redux/slices/checkoutSlice';
import {
  addItem,
  itemCustomizationReducer,
  resetModifiers,
  sizeSelection,
  updateModifiers,
  updatePriceForAllItem,
  updatePriceForItem,
  updateSpecificItemSelectedModifiers,
} from 'src/redux/slices/itemCustomizationSlice';
import { itemSliceReducer } from 'src/redux/slices/itemSlice';
import { orderDetailSliceReducer } from 'src/redux/slices/orderDetailSlice';

const ITEM_NO = CONSTANT.SINGLE_ITEM_SELECTED_SECTION;

/** Same reducer map as src/redux/store/store.ts (minus redux-persist). */
const siteReducers = combineReducers({
  location: locationSliceReducer,
  cart: cartReducer,
  user: userSliceReducer,
  itemCustomization: itemCustomizationReducer,
  Items: itemSliceReducer,
  checkout: checkoutReducer,
  orderDetails: orderDetailSliceReducer,
  addPayment: addPaymentSliceReducer,
});

/**
 * A private store for one Berry customization. Every slice except
 * `itemCustomization` starts as a copy of the site's live state (store,
 * order type, user) so the site's customization components read the same
 * context they would on the item page; `itemCustomization` starts empty.
 */
export function createSiteCustomizationStore(siteState: Record<string, any>) {
  const { itemCustomization: _ignored, ...rest } = siteState ?? {};
  return configureStore({
    reducer: siteReducers,
    preloadedState: _clonedeep(rest) as any,
    // The engine passes its bucket objects through action payloads and the
    // reducer updates them (see updatePriceForItem) — the site's store
    // tolerates this, so the dev-only checks are off here too.
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({ serializableCheck: false, immutableCheck: false }),
  });
}

export type SiteCustomizationStore = ReturnType<typeof createSiteCustomizationStore>;

export function snapshotItemBuckets(): any[] {
  return _clonedeep(itemBuckets.getBuckets() ?? []);
}

/**
 * Puts the site's buckets back, then makes the site's own customization view
 * re-render. The site's ingredient cards read their counts straight from the
 * buckets on every render (AddIngredients → selectedCore/selectedAddOns), so
 * an item page that re-rendered while Berry's buckets were live (e.g. on
 * Berry's cart write) would otherwise keep showing — and build its next click
 * on — Berry's counts. The dispatch keeps the page's state identical; only the
 * array reference changes, which is enough for its `useSelector` to re-render.
 */
export function restoreItemBuckets(snapshot: any[], siteStore?: { getState: () => any; dispatch: (a: any) => any }): void {
  itemBuckets.resetBuckets();
  const live = itemBuckets.getBuckets();
  (snapshot ?? []).forEach((entry) => live.push(entry));
  if (siteStore) {
    const modifiers = siteStore.getState()?.itemCustomization?.modifiers ?? [];
    siteStore.dispatch(resetModifiers({ modifiers: [...modifiers] }));
  }
}

/**
 * Item-page start-up for a single (non-package) item, in the page's order:
 * bucket creation (OtherManuItems mount effect) → addItem (OtherManuItems
 * `item` effect) → core memory chip + ingredient sync
 * (prepareBucketAndStartSyncForSingleItem) → size (ItemDetail `item` effect).
 */
export function initSingleItemCustomization(store: SiteCustomizationStore, item: any): void {
  const { dispatch } = store;
  itemBuckets.createComboBucket({
    keys: [
      CONSTANT.REQUIRED_MODIFIERS,
      CONSTANT.CORE_MODIFIERS,
      CONSTANT.ADD_ONS,
      CONSTANT.COMPLIMENTARY_MODIFIER,
      CONSTANT.ITEM_AS_MODIFIERS,
    ],
    items: ITEM_NO,
    handleItemOwnModifiers: false,
  });
  dispatch(addItem(item));
  itemBuckets.addCoreModifierMemoryChip(ITEM_NO, item?.bucket, item?.items_modifier_groups);
  syncIngredients(store, item);
  if (!item?.half_brink_item_id) {
    dispatch(sizeSelection({ size: CONSTANT.FULL_SIZE.id, brink_id: item.full_brink_item_id }));
  }
}

/** OtherManuItems handleSyncIngredients — non-edit path (Berry never edits a cart line). */
function syncIngredients(store: SiteCustomizationStore, item: any): void {
  // handleCoreModifiers
  let ingredients: any[] = _clonedeep(availableModifiers(item));
  if (ingredients?.length) ingredients = sortBasedOnPriceByModifierGroups(ingredients);
  (ingredients ?? []).forEach((ingredient) => {
    const quantity = parseInt(ingredient.quantity, 10) || 1;
    calculatePriceForItem({
      currentModifier: {
        modifier_id: ingredient.id,
        modifier_name: ingredient.name,
        modifier_group_id: ingredient.modifier_group_id,
        modifier_calories: ingredient.calories,
        brink_id: ingredient.brink_modifier_id,
        quantity,
        brink_modifier_group_id: ingredient.brink_modifier_group_id,
        display_price: ingredient.price,
        original_price: ingredient.original_price,
        core: true,
        type: CONSTANT.BY_DEFAULT,
        modifier_type: CONSTANT.CORE_MODIFIERS,
      } as any,
      item_id: ITEM_NO,
    });
  });

  // handleDefaultModifiers
  (item.complementary_items ?? []).forEach((group: any) => {
    group.modifiers_groups_modifiers?.forEach((selectedItem: any) => {
      if (!selectedItem.is_selected) return;
      calculatePriceForItem({
        currentModifier: createPayloadToSync(
          { ...selectedItem, modifier_type: CONSTANT.COMPLIMENTARY_MODIFIER },
          item,
        ),
        item_id: CONSTANT.COMPLEMENTARY_SELECTED_SECTION_FOR_SINGLE_ITEM,
      });
    });
  });
  (item.is_selected ?? []).forEach((selectedItem: any) => {
    calculatePriceForItem({
      currentModifier: createPayloadToSync(
        { ...selectedItem, modifier_type: CONSTANT.CORE_MODIFIERS },
        item,
      ),
      item_id: ITEM_NO,
    });
  });

  // handleSync86Ingredients — out-of-stock defaults are taken off.
  unavailableModifiers(item).forEach((ingredient: any) => {
    calculatePriceForItem({
      currentModifier: {
        modifier_id: ingredient.id,
        modifier_name: ingredient.name,
        modifier_group_id: ingredient.modifier_group_id,
        modifier_group_min: ingredient.modifier_group_min,
        modifier_calories: ingredient.calories,
        brink_id: ingredient.brink_modifier_id,
        quantity: 0,
        brink_modifier_group_id: ingredient.brink_modifier_group_id,
        display_price: ingredient.price,
        type: CONSTANT.DECREASE,
        modifier_type: CONSTANT.CORE_MODIFIERS,
      } as any,
      item_id: ITEM_NO,
    });
  });

  // updateRedux
  const priceArray: any[] = [];
  itemBuckets.getBuckets()?.forEach((currentItemBucket: any) => {
    Object.keys(currentItemBucket?.bucket ?? {}).forEach((type) => {
      priceArray.push({
        activeBucket: currentItemBucket.bucket[type],
        itemId: currentItemBucket?.item === 2 ? CONSTANT.COMPLEMENTARY_ITEM : 1,
      });
    });
  });
  store.dispatch(
    updateModifiers([
      { itemModifiers: modifiersAgainstAnItem({ itemId: ITEM_NO }), itemId: 1 },
      {
        itemModifiers: modifiersAgainstAnItem({
          itemId: CONSTANT.COMPLEMENTARY_SELECTED_SECTION_FOR_SINGLE_ITEM,
        }),
        itemId: CONSTANT.COMPLEMENTARY_ITEM,
      },
    ]),
  );
  store.dispatch(updatePriceForAllItem(priceArray));
}

/** OtherManuItems handleIngredientSelection (single item; no tray chips). */
export function selectIngredient(store: SiteCustomizationStore, data: any, id: number = ITEM_NO): void {
  const { itemId, activeBucket, itemModifiers } = calculatePriceForItem({
    currentModifier: data,
    item_id: id as any,
  }) as any;
  store.dispatch(updateSpecificItemSelectedModifiers({ itemModifiers, itemId }));
  if (activeBucket !== undefined) {
    store.dispatch(updatePriceForItem({ itemId, activeBucket }));
  }
}

/** Unit price exactly as the item page shows it: item price + customization (ItemDetail `price()`). */
export function siteUnitPrice(order: any, item: any): number {
  return toFixedNumber(getCustomizedItemPrice(order, item?.price) + (order?.display_price ?? 0));
}

/**
 * Required groups not yet satisfied — ItemDetail checkIfAllRequiedSelected
 * (PARENT_ITEM_REQUIRED_MODIFIERS). Returns the first missing group's label,
 * or null when the item can be added. Salad trays have no required groups
 * today (their only min-1 group, Greens, is a substitution that always has a
 * pick), so this normally passes; it is here so the rule matches the site if
 * Pulse adds one.
 */
export function missingRequiredGroup(item: any): string | null {
  const bucket = itemBuckets.getSingleBucket({
    name: CONSTANT.REQUIRED_MODIFIERS,
    fromItem: ITEM_NO,
  });
  for (const group of item?.items_required_modifier_groups ?? []) {
    const picked = (bucket?.additionalAdded?.modifiers ?? []).filter(
      (m: any) => m.modifier_group_id === group.id,
    );
    const noDressing = bucket?.noDressings?.find((g: any) => g.id === group.id && g.noDressing);
    if (picked.length < (group.min ?? 0) && !noDressing) {
      return group.label || group.name || 'a required choice';
    }
  }
  return null;
}

/**
 * The cart line the item page sends for this customization —
 * ItemDetail useAddToOrderPayloadStructure.generateOrderStructure, then
 * useAddToOrder createOrderObj + dataToSyncBackendRequest (set_id, quantity,
 * createOrderPayloadModifiers). Berry's widget wraps it in the same cart
 * payload as every other Berry add.
 */
export function buildSiteCartItem(
  order: any,
  item: any,
  quantity: number,
): Record<string, any> {
  const itemWithComplementary = creatingModifiersForItems(_clonedeep(order?.modifiers ?? []), 1);
  const structuredOrder: Record<string, any> = {
    item_id: order?.item_id,
    display_price: toFixedNumber(
      (order?.display_price ?? 0) + getCustomizedItemPrice(order, item?.price),
    ),
    item_size: itemSize(order?.size, item?.half_brink_item_id),
    name: order?.name,
    modifiers: [],
    item_calories: order?.item_calories,
    category_name: item?.category_name,
    make_time_minutes: item?.make_time_minutes,
    recipient_name: '',
    special_instructions: '',
  };
  itemWithComplementary?.forEach((current: any) => {
    if (current?.item) structuredOrder.modifiers.push(...(current.modifiers ?? []));
    else structuredOrder.modifiers.push(current);
  });

  // "No dressing" lines (ItemDetail: "DON'T REMOVE").
  const requiredBucket = itemBuckets.getSingleBucket({
    name: CONSTANT.REQUIRED_MODIFIERS,
    fromItem: ITEM_NO,
  });
  (requiredBucket?.noDressings ?? []).forEach((dressingItem: any) => {
    if (!dressingItem?.noDressing) return;
    const group = item?.items_required_modifier_groups?.find((e: any) => e?.id === dressingItem.id);
    structuredOrder.modifiers.push({
      modifier_id: 0,
      modifier_name: group?.label,
      modifier_label: `No ${group?.label}`,
      display_price: 0,
      modifier_calories: 0,
      brink_id: 0,
      quantity: 0,
      modifier_group_min: 0,
      max: 0,
      type: CONSTANT.SELECTED,
      code: Codes.NO,
      extendableLimitValue: 0,
      modifier_group_id: group?.id,
      modifier_group_max: group?.max,
      in_item: 0,
      in_slide: false,
      modifier_type: CONSTANT.REQUIRED_MODIFIERS,
      additionalPrice: 0,
      is_no_modifier_selected: 1,
    });
  });

  const line = {
    ...structuredOrder,
    id: structuredOrder.item_id,
    price: structuredOrder.display_price ? structuredOrder.display_price : item?.price,
    category_id: String(item?.category_id ?? ''),
    free: false,
    set_id: 1,
    quantity: quantity ?? 1,
  };
  return createOrderPayloadModifiers(line, false);
}

/**
 * Readable lines for Berry's review step, read from the cart line that will
 * be sent (buildSiteCartItem) so the review lists exactly what the kitchen
 * gets: removed included ingredients ("No …"), extra servings of included
 * ones, swaps and added extras, each with what it adds to the tray price.
 * Captured site line (2026-10-07, item 255): removed core = code "No" /
 * quantity 0; extra core = core_modifiers "ADD"; extras and swaps =
 * core_related_modifiers / add_ons "ADD" (`substitute_with_id` on a swap).
 */
export function describeSiteCustomization(cartItem: Record<string, any>): string[] {
  const lines: string[] = [];
  (cartItem?.modifiers ?? []).forEach((m: any) => {
    const name = String(m.modifier_name ?? m.name ?? '').trim();
    if (!name) return;
    const quantity = Number(m.quantity ?? 0);
    if (quantity === 0 || String(m.code ?? '').toUpperCase() === Codes.NO) {
      lines.push(m.modifier_label || `No ${name}`);
      return;
    }
    const extra = Number(m.additionalPrice ?? 0);
    const label = m.modifier_type === CONSTANT.CORE_MODIFIERS ? `Extra ${name}` : name;
    const qtyLabel = quantity > 1 ? ` × ${quantity}` : '';
    lines.push(extra > 0 ? `${label}${qtyLabel} (+$${extra.toFixed(2)})` : `${label}${qtyLabel}`);
  });
  return lines;
}
